import classNames from "classnames";
import React, { isValidElement, ReactElement, useEffect, useMemo, useState } from "react";
import { CollapseProps } from "./collapse.types";

/**
 * This is a low level atom component that can be used to conditionally show or hide UI content.
 * It uses the `children` prop as UI content to be shown/hidden.
 *
 * By default, it does so by physically adding or removing the child element to/from the DOM.
 *
 * However, for some use cases (SEO sensitive content) it may be desired that the element be always present in the DOM
 * and only be _visually_ hidden via CSS.
 *
 * For such use cases, set the `preserveDomElement` prop to `true`. In this case this component uses the HTML `hidden`
 * attribute instead. Note that this only works for `children` that is a valid React Element. It is ignored for
 * `children` that are either `strings` or `numbers` or `null`.
 */
export const Collapse = ({
  expanded = false,
  preserveDomElement = false,
  lazyInitialCollapse = false,
  children,
}: CollapseProps): ReactElement<CollapseProps> | null => {
  const [lazyExpanded, setLazyExpanded] = useState(lazyInitialCollapse ? true : expanded);

  useEffect(() => {
    setLazyExpanded(expanded);
  }, [expanded]);

  const elementToRender = useMemo(() => {
    if (preserveDomElement && isValidElement(children)) {
      const mayBeClassNameWithHidden = classNames(children.props.className, { "hidden": !lazyExpanded });
      return React.cloneElement(children as ReactElement, { className: mayBeClassNameWithHidden });
    } else {
      return lazyExpanded ? <>{children}</> : null;
    }
  }, [children, lazyExpanded, preserveDomElement]);

  return elementToRender;
};
